import axios, {
    AxiosError,
    AxiosHeaders,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
    RawAxiosResponseHeaders
} from "axios";
import { openModal } from "src/redux/actions";
import store from "src/redux/store";
import { clearNonMatrixLocalStorage } from "src/scenes/Chat/utils/matrixStorage";
import {
    currentPathIsPublicMeeting,
    currentPathIsPublicUnauthenticated,
    currentPathSegment,
} from "src/scenes/App/App";

export interface ApiMessageErrorBody {
    error: string;
}

export interface ApiFormErrorBody {
    error: {
        fields: string[];
        messages: string[];
    };
}

export interface ApiPermissionErrorBody {
    error: string;
    permissions: string[];
}

export interface ApiPolicyErrorBody {
    error: string;
    policyType: string;
}

type ApiErrorBody =
    | ApiMessageErrorBody
    | ApiFormErrorBody
    | ApiPermissionErrorBody
    | ApiPolicyErrorBody;

enum ApiErrorBodyType {
    ApiMessageError,
    ApiFormError,
    ApiPermissionError,
    ApiPolicyError,
    UnknownError
}

export interface ApiFieldError {
    field: string;
    message: string;
}

export type ApiErrorDetails =
    | { type: ApiErrorBodyType.ApiMessageError; message: string }
    | { type: ApiErrorBodyType.ApiFormError; message: string; fields: ApiFieldError[] }
    | { type: ApiErrorBodyType.ApiPermissionError; message: string; permissions: string[] }
    | { type: ApiErrorBodyType.ApiPolicyError; message: string; policyType: string }
    | { type: ApiErrorBodyType.UnknownError; message: string };

type PolicyAcceptanceHandler = (policyType: string) => void;

const UNKNOWN_ERROR_MESSAGE = "Something went wrong. Unknown error.";
const UNAUTHORIZED_ERROR_MESSAGE = "Unauthorized access.";
const LOGIN_REDIRECT_DELAY_MS = 1500;
const LOGIN_PATHS = ["login", "licenseRenewal", "forgotPassword", "forgotAccessCode"];
const API_INVALID_TOKEN_MESSAGE: string = "Unauthorised access, please log in";

export enum ErrorCategory {
    AutomaticLogout,
    PolicyAcceptance,
    PermissionDenial,
    SilentFailure,
    UnknownFailure
}

let policyAcceptanceHandler: PolicyAcceptanceHandler | undefined;
let interceptorId: number | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null;
};

const isStringArray = (value: unknown): value is string[] => {
    return Array.isArray(value) && value.every((item: unknown): item is string => typeof item === "string");
};

const parseApiErrorDetails = (error: AxiosError<ApiErrorBody>): ApiErrorDetails => {
    const data: ApiErrorBody | undefined = error.response?.data;
    if (!error.response && error.request) {
        return { type: ApiErrorBodyType.UnknownError, message: error.message };
    }

    if (!isRecord(data)) {
        return { type: ApiErrorBodyType.UnknownError, message: error.message || UNKNOWN_ERROR_MESSAGE };
    }

    const responseError = data.error;
    if (typeof responseError === "string") {
        if (isStringArray(data.permissions)) {
            return { type: ApiErrorBodyType.ApiPermissionError, message: responseError, permissions: data.permissions };
        }

        if (typeof data.policyType === "string") {
            return { type: ApiErrorBodyType.ApiPolicyError, message: responseError, policyType: data.policyType };
        }

        return { type: ApiErrorBodyType.ApiMessageError, message: responseError };
    }

    if (
        isRecord(responseError)
        && isStringArray(responseError.fields)
        && isStringArray(responseError.messages)
        && responseError.fields.length === responseError.messages.length
    ) {
        const fields: ApiFieldError[] = responseError.fields.map((field: string, index: number): ApiFieldError => ({
            field,
            message: responseError.messages[index]
        }));
        return { type: ApiErrorBodyType.ApiFormError, message: responseError.messages.join(", "), fields };
    }

    if (isRecord(responseError) && typeof responseError.messages === "string") {
        return { type: ApiErrorBodyType.ApiMessageError, message: responseError.messages };
    }

    const responseErrorEntries = isRecord(responseError) ? Object.entries(responseError) : [];
    if (
        responseErrorEntries.length > 0
        && responseErrorEntries.every(([, message]: [string, unknown]) => typeof message === "string")
    ) {
        const fields: ApiFieldError[] = responseErrorEntries
            .map(([field, message]): ApiFieldError => ({ field, message: String(message) }));
        return { type: ApiErrorBodyType.ApiFormError, message: fields.map(({ message }) => message).join(", "), fields };
    }

    if (typeof data.policyType === "string") {
        return { type: ApiErrorBodyType.ApiPolicyError, message: UNAUTHORIZED_ERROR_MESSAGE, policyType: data.policyType };
    }

    return { type: ApiErrorBodyType.UnknownError, message: error.message || UNKNOWN_ERROR_MESSAGE };
};

export class ApiSuccessResponse<TData = unknown> implements AxiosResponse<TData> {
    data: TData;
    status: number;
    statusText: string;
    headers: RawAxiosResponseHeaders | AxiosHeaders;
    config: InternalAxiosRequestConfig;
    request?: unknown;

    constructor(response: AxiosResponse<TData>) {
        this.data = response.data;
        this.status = response.status;
        this.statusText = response.statusText;
        this.headers = response.headers;
        this.config = response.config;
        this.request = response.request;
    }
}

export class ApiErrorResponse<TData = ApiErrorBody> extends Error {
    private readonly details: ApiErrorDetails;
    readonly status: number | undefined;
    readonly data: TData | undefined;
    readonly response: AxiosResponse<TData> | undefined;
    readonly request: unknown;
    readonly config: AxiosRequestConfig | undefined;
    readonly code: string | undefined;
    readonly isCanceled: boolean;
    readonly __CANCEL__?: boolean;

    constructor(error: AxiosError<TData>) {
        const details = parseApiErrorDetails(error as AxiosError<ApiErrorBody>);
        super(details.message);
        this.name = "ApiErrorResponse";
        this.details = details;
        this.status = error.response?.status;
        this.data = error.response?.data;
        this.response = error.response;
        this.request = error.request;
        this.config = error.config;
        this.code = error.code;
        this.isCanceled = axios.isCancel(error);
        this.__CANCEL__ = this.isCanceled ? true : undefined;
    }

    getApiErrorDetails(): ApiErrorDetails {
        return this.details;
    }

    getStatus(): number | undefined {
        return this.status;
    }

    hasFormErrors(): boolean {
        return this.details.type === ApiErrorBodyType.ApiFormError;
    }

    getFormErrors(): readonly ApiFieldError[] {
        if (this.details.type === ApiErrorBodyType.ApiFormError) {
            return this.details.fields;
        }

        return [];
    }

    getApiErrorMessages(): string[] {
        const formErrors = this.getFormErrors();
        if (formErrors.length > 0) {
            return formErrors.map(({ message }: ApiFieldError): string => message);
        }

        return this.details.message.split("\n");
    }

    getErrorCategory(): ErrorCategory {

        const details = this.getApiErrorDetails();
        if (this.status === 401) {
            if (details.type === ApiErrorBodyType.ApiPolicyError) {
                return ErrorCategory.PolicyAcceptance;
            }
            if (this.getApiErrorDetails().message === API_INVALID_TOKEN_MESSAGE) {
                return ErrorCategory.AutomaticLogout;
            }
            if (
                LOGIN_PATHS.includes(currentPathSegment())
                || currentPathIsPublicUnauthenticated()
                || currentPathIsPublicMeeting()
            ) {
                return ErrorCategory.SilentFailure;
            }
        }

        if (this.status === 403) {
            if (currentPathIsPublicMeeting()) {
                return ErrorCategory.SilentFailure;
            }
            if (this.getApiErrorDetails().type === ApiErrorBodyType.ApiPermissionError) {
                return ErrorCategory.PermissionDenial;
            }
        }

        return ErrorCategory.UnknownFailure;
    }
}

export const setPolicyAcceptanceHandler = (handler: PolicyAcceptanceHandler): void => {
    policyAcceptanceHandler = handler;
};

const handleRejectedResponse = (error: unknown): Promise<never> => {
    const apiError = error instanceof ApiErrorResponse
        ? error as ApiErrorResponse
        : new ApiErrorResponse(axios.isAxiosError(error) ? error : AxiosError.from(error));

    const category = apiError.getErrorCategory();
    if (category === ErrorCategory.PolicyAcceptance) {
        policyAcceptanceHandler?.(apiError.getApiErrorDetails().policyType);
    }
    if (category === ErrorCategory.PermissionDenial) {
        store.dispatch(openModal());
    }
    if (category === ErrorCategory.AutomaticLogout) {
        window.setTimeout((): void => {
            clearNonMatrixLocalStorage();
            window.location.href = "/login?status=invalidToken";
        }, LOGIN_REDIRECT_DELAY_MS);
    }

    return Promise.reject(apiError);
};

export const installAxiosResponseInterceptor = (): number => {
    if (interceptorId !== undefined) {
        return interceptorId;
    }

    interceptorId = axios.interceptors.response.use(
        (response: AxiosResponse): ApiSuccessResponse => new ApiSuccessResponse(response),
        (error: unknown): Promise<never> => handleRejectedResponse(error)
    );
    return interceptorId;
};
